import React, { useState, useEffect } from 'react'
import { useTheme } from 'next-themes'

interface BackgroundSlideshowProps {
  images: string[]
  interval?: number
  className?: string
}

const BackgroundSlideshow: React.FC<BackgroundSlideshowProps> = ({ 
  images, 
  interval = 5000,
  className = ""
}) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const { theme } = useTheme()
  const [currentTheme, setCurrentTheme] = useState('light')

  // Update theme when it changes
  useEffect(() => {
    const updateTheme = () => {
      const isDark = document.documentElement.classList.contains('dark')
      setCurrentTheme(isDark ? 'dark' : 'light')
    }
    
    updateTheme()
    
    const observer = new MutationObserver(updateTheme)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    })
    
    return () => observer.disconnect()
  }, [theme])

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length)
    }, interval)

    return () => clearInterval(timer)
  }, [images.length, interval])

  return (
    <div className={`fixed inset-0 z-0 ${className}`}>
      {images.map((image, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={image}
            alt={`Background slide ${index + 1}`}
            className="w-full h-full object-cover"
          />
          {/* Theme-aware overlay for better text readability */}
          <div className={`absolute inset-0 ${
            currentTheme === 'dark' ? 'bg-black/70' : 'bg-white/90'
          }`}></div>
        </div>
      ))}
    </div>
  )
}

export default BackgroundSlideshow
